import { Injectable, PLATFORM_ID, WritableSignal, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { addDays, nextMonday, parseISO, previousFriday, subDays } from 'date-fns';

const LS_KEY = 'currentDate';

@Injectable({ providedIn: 'root' })
export class DateService {
  private readonly platformId = inject(PLATFORM_ID);

  readonly currentDate: WritableSignal<Date> = signal(this._load());

  private _load(): Date {
    if (!isPlatformBrowser(this.platformId)) return new Date();

    // 1. Try the new key written by this service:
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      try {
        return parseISO(raw);
      } catch { /* fall through */ }
    }

    // 2. Try migrating from the NGXS @@STATE key:
    try {
      const ngxsRaw = localStorage.getItem('@@STATE');
      if (ngxsRaw) {
        const parsed = JSON.parse(ngxsRaw);
        const dateStr = parsed?.calendar?.currentDate;
        if (dateStr) {
          const date = parseISO(dateStr);
          this._persist(date);
          return date;
        }
      }
    } catch { /* fall through */ }

    return new Date();
  }

  private _persist(date: Date): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(LS_KEY, date.toISOString());
    }
  }

  setCurrentDate(date: Date): void {
    this.currentDate.set(date);
    this._persist(date);
  }

  setCurrentDateToNextWeekDay(): void {
    const date = this.currentDate();
    if ([5, 6].includes(date.getDay())) {
      this.setCurrentDate(nextMonday(date));
    } else {
      this.setCurrentDate(addDays(date, 1));
    }
  }

  setCurrentDateToPreviousWeekDay(): void {
    const date = this.currentDate();
    if ([0, 1].includes(date.getDay())) {
      this.setCurrentDate(previousFriday(date));
    } else {
      this.setCurrentDate(subDays(date, 1));
    }
  }
}
